import { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../../components/Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";

import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { fetchAllDrivers } from "../IncidentPopup/IncidentPopup";
import TruckPopup from "../TruckPopup/TruckPopup";
import TrailerPopup from "../TrailerPopup/TrailerPopup";
import {
  makeFieldConfigDynamic,
  rentFieldConfiguration,
  rentInitialValues,
} from "../../../formConfigs/Fleet/rentFieldConfig";
import useAuth from "../../../hooks/useAuth";
import { fetchAssets } from "../RentPopup/RentPopup";
import { apiVariable } from "../../../api/apiVariable";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import dayjs from "dayjs";
import CompanyPopup from "../CompanyPopup/CompanyPopup";
import DriverPopup from "../DriverPopup/DriverPopup";
import { useMsal } from "@azure/msal-react";

interface UpdateRentPopupProps {
  rent: any;
}

const UpdateRentPopup = ({ rent }: UpdateRentPopupProps) => {
  const [open, setOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<string>(
    rent.truckId ? "Truck" : "Trailer",
  );
  const [initialValues, setInitialValues] = useState(rentInitialValues);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const user = useMsal();
  const userName = user.accounts[0].name;

  const { data: assetData, isLoading: isLoadingAssets } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: open,
  });

  const { data: ownerCompanies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: open,
  });

  const { data: allDrivers, isLoading: isLoadingDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open,
  });

  const isLoading = isLoadingAssets || isLoadingCompanies || isLoadingDrivers;

  useEffect(() => {
    if (open && rent && assetData && ownerCompanies && allDrivers) {
      setInitialValues({
        assetAffected: rent.truckId ? "Truck" : "Trailer",
        assetNumber: assetData.find(
          (a: any) => a.id === (rent.truckId || rent.trailerId),
        ),
        authorityCompanyId: ownerCompanies.find(
          (c: any) => c.id === rent.authorityCompanyId,
        ),
        billToId: ownerCompanies.find((c: any) => c.id === rent.billToId),
        soldToId: ownerCompanies.find((c: any) => c.id === rent.soldToId) ?? "",
        driverId: allDrivers.find((d: any) => d.id === rent.driverId),
        startDate: dayjs(rent.startDate).format("YYYY-MM-DD"),
        endDate: rent.endDate ? dayjs(rent.endDate).format("YYYY-MM-DD") : null,
        transactionType: rent.transactionType,
        rentalTerm: rent.rentalTerm,
        billingFrequency: rent.billingFrequency,
        baseRentalRate: rent.baseRentalRate,
        mileageRate: rent.mileageRate,
        leaseTerm: rent.leaseTerm,
        downPayment: rent.downPayment,
        mileageCaps: rent.mileageCaps,
        startMileage: rent.startMileage ?? 0,
        endMileage: rent.endMileage ?? 0,
        remainingBalance: rent.remainingBalance,
      });
    }
  }, [open, rent, assetData, ownerCompanies, allDrivers]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/rent`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["rents"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update rent");
    },
  });

  const submitHandler = (data: any) => {
    const submitData = {
      ...data,
      id: rent.id,
      truckId: selectedAssetType === "Truck" ? getId(data.assetNumber) : null,
      trailerId:
        selectedAssetType === "Trailer" ? getId(data.assetNumber) : null,
      billToId: getId(data.billToId),
      soldToId: data.soldToId ? getId(data.soldToId) : null,
      authorityCompanyId: getId(data.authorityCompanyId),
      driverId: getId(data.driverId),
      addedBy: userName,
      downPayment: data.downPayment === "" ? null : Number(data.downPayment),
      milageRate: data.mileageRate === "" ? null : Number(data.mileageRate),
      startMileage: data.startMileage === "" ? null : Number(data.startMileage),
      endMileage: data.endMileage === "" ? null : Number(data.endMileage),
      endDate: data.endDate ? data.endDate : null,
    };
    delete submitData.assetAffected;
    delete submitData.assetNumber;
    updateMutation.mutate(submitData);
  };

  const onValuesChange = (
    values: any,
    setFieldValue?: (field: string, value: any) => void,
  ) => {
    const assetType = values.assetAffected;
    setSelectedAssetType(assetType);

    // Calculate mileage caps if all required values are present and valid
    if (values.mileageRate) {
      const startMileage = values.startMileage
        ? Number(values.startMileage)
        : 0;
      const endMileage = values.endMileage ? Number(values.endMileage) : 0;
      const mileageRate = Number(values.mileageRate);

      if (!isNaN(startMileage) && !isNaN(endMileage) && !isNaN(mileageRate)) {
        const calculatedCaps = (endMileage - startMileage) * mileageRate;
        // Only update if the value has changed to prevent infinite loop
        if (setFieldValue && calculatedCaps !== values.mileageCaps) {
          setFieldValue("mileageCaps", calculatedCaps);
        }
      }
    }
  };

  const dynamicFields = makeFieldConfigDynamic(
    rentFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType,
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Update
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={initialValues}
        title="Update Rent"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={{
          assets: assetData,
          billTos: ownerCompanies,
          soldTos: ownerCompanies,
          drivers: allDrivers,
          authorityCompanies: ownerCompanies,
        }}
        loading={updateMutation.isPending || isLoading}
        onValuesChange={onValuesChange}
        fieldComponents={{
          assetNumber: (
            <Box display="flex" justifyContent="center" alignItems="center">
              {selectedAssetType === "Trailer" ? (
                <TrailerPopup />
              ) : (
                <TruckPopup />
              )}
            </Box>
          ),
          authorityCompanyId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
          billToId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
          soldToId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
          driverId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <DriverPopup />
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Rent updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateRentPopup;
